import { ActivityEntityName, Contact, CrmSecurityRight, DomainType, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { CrmRouteName } from '@jms/tenant/module.jms.crm/src/model';
import type { EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';

// Customer entity manifest: Manage + compact create Form, the card both portals reuse,
// and the semantic relation builders its relation tabs mount.

const card: EntityManifestCard<Contact> = {
  component: () => import('./entity.card.renderer/customer.entity.card.renderer.component')
    .then(module => module.CustomerEntityCardRendererComponent),
  resolution: 'if-needed',
  // A trading-as row is the picked party itself; fetching the account's card would rename it back (WBS-11914)
  isComplete: customer => !!((customer as { tradingAs?: string | null }).tradingAs || customer.mobile || customer.phone || customer.workPhone || customer.email),
  summarize: customer => ({ title: customer.name ?? 'Not set' })
};

export const customerManifest = registerManifest<Contact>({
  entity: ActivityEntityName.Customer,
  slug: 'customer',
  presentation: {
    card,
  },

  domains: {
    [DomainType.ClientPortal]: {
      presentation: {
        card,
      },
    },
    [DomainType.ContractorPortal]: {
      presentation: {
        card,
      },
    },
  },

  routes: {
    root: `/${CrmRouteName.ROOT}/${CrmRouteName.Customer}`,
  },

  surfaces: {
    [SurfaceType.Manage]: () => import('./surface/manage/customer.manage.surface').then(module => module.customerManageSurface),
    [SurfaceType.Form]: () => import('./surface/create/customer.create.surface').then(module => module.customerCreateFormSurface),
  },
  operations: {
    create: {
      surface: SurfaceType.Form,
      presentation: { mode: 'dialog', width: 'min(920px, calc(100vw - 40px))' },
      // Customers Create offers every customer type, service providers included (WBS-12233).
      authorize: ({ injector }) => injector.get(RightsService).hasRights(CrmSecurityRight.CustomersCreate),
    },
    // Customers Edit decides the surface's readonly rule for every customer type.
    edit: { surface: SurfaceType.Manage },
  },

  relations: {
    [ActivityEntityName.ServicePlan]: () => import('../service.plan/relations/service.plans.relation').then(module => module.servicePlansRelation(ActivityEntityName.Customer)),
    [ActivityEntityName.Visit]: () => import('./relations/customer.visits.relation').then(module => module.customerVisitsBoardBuilder),
    [ActivityEntityName.VisitApproval]: () => import('./relations/customer.visit.approvals.relation').then(module => module.customerVisitApprovalsBoardBuilder),
    [ActivityEntityName.ServiceHistory]: () => import('../service.history/relations/service.history.relation').then(module => module.serviceHistoryRelation(ActivityEntityName.Customer)),
    [ActivityEntityName.Contact]: () => import('./relations/customer.contacts.relation').then(module => module.crmContactsBoardBuilder),
    [ActivityEntityName.AccountSite]: () => import('./relations/customer.sites.relation').then(module => module.crmSitesBoardBuilder),
    [ActivityEntityName.ManagedSite]: () => import('./relations/customer.managed.sites.relation').then(module => module.crmManagedSitesBoardBuilder),
    [ActivityEntityName.Asset]: () => import('./relations/customer.assets.relation').then(module => module.crmAssetsBoardBuilder),
    [ActivityEntityName.Job]: () => import('./relations/customer.jobs.relation').then(module => module.crmJobsBoardBuilder),
    [ActivityEntityName.RecurringJob]: () => import('./relations/customer.recurring.jobs.relation').then(module => module.crmRecurringJobsBoardBuilder),
    [ActivityEntityName.Opportunity]: () => import('./relations/customer.opportunities.relation').then(module => module.crmOpportunitiesBoardBuilder),
    [ActivityEntityName.QuotePro]: () => import('./relations/customer.quotes.relation').then(module => module.crmQuotesBoardBuilder),
    [ActivityEntityName.Task]: () => import('./relations/customer.tasks.relation').then(module => module.crmTasksBoardBuilder),
    [ActivityEntityName.File]: () => import('./relations/customer.files.relation').then(module => module.crmFilesBoardBuilder),
    [ActivityEntityName.Activity]: () => import('./relations/customer.activities.relation').then(module => module.crmActivitiesBoardBuilder),
  },
});
